/**
 * Domain names for the generated schema, plus the few shapes the OpenAPI
 * document only describes as an opaque object.
 *
 * Everything here is a re-spelling of what the server already defines. The
 * hand-written parts are marked; they mirror `cms_store::ReportSnapshot`,
 * `cms_core::Evaluation` and `cms_store::DraftAnswer`, which the document
 * carries as `Object` because they are generated by `schemars`, not `utoipa`.
 */
import type { components } from "./schema.ts";

type Schemas = components["schemas"];

export type Topic = Schemas["Topic"];
export type Product = Schemas["Product"];
export type Capability = Schemas["Capability"];
export type Question = Schemas["Question"];
export type Answer = Schemas["Answer"];
export type Author = Schemas["Author"];
export type Persona = Schemas["Persona"];
export type Requirement = Schemas["Requirement"];
export type View = Schemas["View"];
export type Questionnaire = Schemas["Questionnaire"];
export type Relevancy = Schemas["Relevancy"];
export type RelevancyQuestionnaire = Schemas["RelevancyQuestionnaire"];
export type Report = Schemas["Report"];
export type Customer = Schemas["Customer"];
export type User = Schemas["User"];
export type Role = Schemas["Role"];
/** Who the caller is: role, name and the customer whose world they see. */
export type Me = Schemas["Me"];
/** A user together with the token that was just minted; the token is shown once. */
export type IssuedToken = Omit<Schemas["IssuedToken"], "user"> & { user: User };

export type AnswerType = Schemas["AnswerType"];
export type AnswerValue = Schemas["AnswerValue"];
export type AnswerOption = Schemas["AnswerOption"];
export type AnswerSource = Schemas["AnswerSource"];
export type Confidence = Schemas["Confidence"];
export type ServerBand = Schemas["Band"];
export type KnockoutMode = Schemas["KnockoutMode"];
export type RequirementMinimum = Schemas["RequirementMinimum"];
/** The five-step ordinal a persona rates a capability on. */
export type RelevancyRating = Schemas["RelevancyRating"];
export type ErrorEnvelope = Schemas["ErrorEnvelope"];
/** One entity standing in the way of a delete. */
export type InUseBy = Schemas["InUseBy"];
export type EvaluationRequest = Schemas["EvaluationRequest"];

export type TreeTopic = Schemas["TreeTopic"];
export type TreeQuestion = Schemas["TreeQuestion"];
/** Whether a report on the requested view reaches a question's answers. */
export type QuestionCoverage = Schemas["QuestionCoverage"];
export type TreeAnswer = Schemas["TreeAnswer"];
export type TreeCapability = Schemas["TreeCapability"];

/** The four states a questionnaire's timestamps derive. */
export type QuestionnaireStatus = "pending" | "received" | "answered" | "evaluated";

/** One staged rating; mirrors `cms_store::DraftRelevancy`. */
export type DraftRelevancy = {
  questionnaire: string;
  capability: string;
  /** Null while the recipient has only left a note, which is allowed. */
  rating: RelevancyRating | null;
  /** Free-text context; informational only, it never enters a score. */
  note: string | null;
};

/**
 * What a relevancy magic link opens. The generated `RelevancyFillView` types the
 * nested entities as opaque objects, so they are named here.
 */
export type RelevancyFillView = {
  questionnaire: RelevancyQuestionnaire;
  status: QuestionnaireStatus;
  author: string;
  persona: Persona;
  groups: { topic: Topic; capabilities: Capability[] }[];
  drafts: DraftRelevancy[];
};

/** One staged answer of a questionnaire; mirrors `cms_store::DraftAnswer`. */
export type DraftAnswer = {
  questionnaire: string;
  product: string;
  question: string;
  value?: AnswerValue | null;
  comment?: string | null;
  explanation?: string | null;
};

/**
 * What a magic link opens. The generated `FillView` types the nested entities as
 * opaque objects, so they are named here.
 */
export type FillView = {
  questionnaire: Questionnaire;
  status: QuestionnaireStatus;
  author: string;
  questions: Question[];
  products: Product[];
  drafts: DraftAnswer[];
};

/** One answer that survived resolution; mirrors `cms_core::ResolvedAnswer`. */
export type ResolvedAnswer = {
  answer: string;
  question: string;
  value: number;
  source: AnswerSource;
  confidence: Confidence;
  author: string;
  questionnaire?: string | null;
};

export type Coverage = {
  answered: number;
  total: number;
};

export type CapabilityEvaluation = {
  capability: string;
  score: number | null;
  band: ServerBand | null;
  coverage: Coverage;
  answers: ResolvedAnswer[];
};

export type TopicEvaluation = {
  topic: string;
  score: number | null;
  band: ServerBand | null;
  effective_weight: number;
  coverage: Coverage;
  capabilities: CapabilityEvaluation[];
};

export type ProductEvaluation = {
  product: string;
  score: number | null;
  coverage: Coverage;
  topics: TopicEvaluation[];
};

export type RequirementViolation = {
  requirement: string;
  capability: string;
  minimum: RequirementMinimum;
  rationale: string;
  actual: number | null;
  actual_band: ServerBand | null;
  contributing: ResolvedAnswer[];
};

/** A must-have whose capability is not assessed: a gap to close, not a failure. */
export type OpenRequirement = {
  requirement: string;
  capability: string;
  minimum: RequirementMinimum;
  rationale: string;
};

export type PersonaProductEvaluation = {
  product: string;
  score: number | null;
  /**
   * The same product as this persona weighs it, and what its ranking sorts by.
   * Absent in snapshots written before persona weighting existed, and where the
   * persona rated away everything the product had a score for.
   */
  weighted_score?: number | null;
  violations: RequirementViolation[];
  open: OpenRequirement[];
  excluded: boolean;
};

/** One persona's ranking; personas are listed side by side and never blended. */
export type PersonaEvaluation = {
  persona: string;
  products: PersonaProductEvaluation[];
};

export type BandConfig = {
  partial_from: number;
  solid_from: number;
  excellent_from: number;
};

export type Evaluation = {
  formula_version: number;
  bands: BandConfig;
  knockout_mode: KnockoutMode;
  requirements_skipped: boolean;
  products: ProductEvaluation[];
  personas: PersonaEvaluation[];
};

/** The input set the engine was fed, kept so a report can name its scope. */
export type EvalInput = {
  topics: Topic[];
  capabilities: Capability[];
  questions: Question[];
  products: Product[];
  answers: Answer[];
  personas: Persona[];
  requirements: Requirement[];
  /** The persona ratings the weighted scores were computed from. */
  relevancies?: Relevancy[];
};

/** What `POST /evaluate` and `GET /reports/{id}` answer with. */
export type ReportSnapshot = {
  report: string;
  as_of: number;
  formula_version: number;
  /** When the report was locked, or null while it still follows changes. */
  locked_at: number | null;
  input: EvalInput;
  evaluation: Evaluation;
};

export type ChangeEvent = {
  id: string;
  entity_type: string;
  entity_id: string;
  diff: string;
  actor: string;
  at: number;
};

/** The status a questionnaire's timestamps derive, per CONCEPT.md. */
export const questionnaireStatus = (questionnaire: Questionnaire): QuestionnaireStatus => {
  if (questionnaire.evaluated_at !== null) return "evaluated";
  if (questionnaire.answered_at !== null) return "answered";
  if (questionnaire.received_at !== null) return "received";
  return "pending";
};

/** The authored options of a question, empty for the types that have none. */
export const answerOptions = (question: Question): AnswerOption[] =>
  question.answer_type.kind === "select" || question.answer_type.kind === "multi_select"
    ? question.answer_type.options
    : [];

/** The status a relevancy questionnaire's timestamps derive. */
export const relevancyStatus = (
  questionnaire: RelevancyQuestionnaire,
): QuestionnaireStatus => {
  if (questionnaire.evaluated_at !== null) return "evaluated";
  if (questionnaire.answered_at !== null) return "answered";
  if (questionnaire.received_at !== null) return "received";
  return "pending";
};

/**
 * The weight one rating step carries, mirroring `cms_core::eval::relevancy`.
 * Shown to make an applied weight auditable; it is never used to compute a score.
 */
export const relevancyWeight = (rating: RelevancyRating): number =>
  ({ much_less: 0, less: 0.25, equal: 0.5, more: 0.75, much_more: 1 })[rating];

/** Text answers never enter the score; they are knowledge base only. */
export const isInformational = (question: Question): boolean =>
  question.answer_type.kind === "text";
